// Tick phase 1 (IMPLEMENTATION §2.4): hold hits and drill counters, then deposit damage → spawns (R1), in
// deposit id order.
//   R3  hold: after the `hold` command, one hit every holdRepeatTicks (the pointerdown `hit` is a command).
//       Shared cooldown (economy.manual.sharedCooldown, GDD D13, lead decision L4): taps and hold hits share
//       one cooldown of holdRepeatTicks. A tap lands at once when the cooldown is over, else it is buffered
//       (≤ bufferedTaps, landed in order at the start of phase 1 once the cooldown is over) or refused
//       (`cooldown`); a hold hit that falls due during the cooldown waits for it. Every manual hit sets the
//       cooldown to holdRepeatTicks and it runs down after phase 1's manual hits, so two manual hits (at
//       dispatch = phase 0, or in phase 1, in any order) always land ≥ holdRepeatTicks ticks apart.
//       drills: integer interval counters (hitTicks by level), power by level in milli; a drill that does
//       not face a deposit stalls NO_TARGET immediately and its counter restarts.
//   R1  while acc ≥ hard, < maxSpawnsPerTick spawns this tick and an output exists: roll a tier from the
//       level's odds (the shared seeded rng), spawn, acc −= hard. No output: acc = min(acc, hard), one bonk
//       per blocked episode, stall VEIN_BLOCKED. Leftover acc carries, capped at accCapHardness × hard.
//       Outputs (rules/push): an adjacent acceptor that takes this ore now (processors and Crates on a
//       non-front side, a chute from its back when its chain sink takes it), round-robin; else free floor.
//       The tier is drawn by PEEKING the next rng value and committed only when the ore spawns, so a blocked
//       deposit consumes no draws and the stream does not depend on how long a vein stayed blocked.

import { insertInto, makeRef } from '../containers.ts';
import { stepCell } from '../core/grid.ts';
import { mb32Next, mb32Out } from '../core/rng.ts';
import type { Cell, Int } from '../core/types.ts';
import type { Ctx } from '../ctx.ts';
import { addItem, depositAt, depositDef, discover, findBuilding } from '../entities.ts';
import type { HitSrc } from '../events.ts';
import { book } from '../ledger.ts';
import { depositOutput } from '../rules/push.ts';
import { oddsForLevel, rollTier } from '../rules/spawn.ts';
import { clearStall, raiseStall } from '../stall.ts';
import type { Building, DepositB, DrillB } from '../state.ts';

/** Adds damage (milli) to a deposit; spawning happens in the tick. `by` = drill id, 0 for the player. */
export function hitDeposit(c: Ctx, dep: DepositB, dmg: Int, src: HitSrc, by: Int): void {
  dep.acc += dmg;
  c.ev.push({ e: 'hit', bid: dep.id, x: dep.x, y: dep.y, src, dmg, by });
}

/**
 * A `hit` command (R3 tap on pointerdown). Returns false when the shared cooldown refuses it (the buffer is
 * full); a buffered tap counts as accepted.
 */
export function tapDeposit(c: Ctx, dep: DepositB): boolean {
  const m = c.d.economy.manual;
  const t = c.s.transient;
  if (!m.sharedCooldown || t.tapCd === 0) {
    hitDeposit(c, dep, m.tapDamageMilli, 'tap', 0);
    if (m.sharedCooldown) t.tapCd = m.holdRepeatTicks;
    return true;
  }
  if (t.tapBuf.length < m.bufferedTaps) {
    t.tapBuf.push({ x: dep.x, y: dep.y });
    return true;
  }
  return false;
}

/** Buffered taps land once the shared cooldown is over (on whatever deposit is still there). */
function landBufferedTaps(c: Ctx): void {
  const m = c.d.economy.manual;
  const t = c.s.transient;
  while (m.sharedCooldown && t.tapCd === 0 && t.tapBuf.length > 0) {
    const p = t.tapBuf.shift() as Cell;
    const dep = depositAt(c, p.x, p.y);
    if (dep === null) continue; // the deposit moved or went: the tap hits nothing
    hitDeposit(c, dep, m.tapDamageMilli, 'tap', 0);
    t.tapCd = m.holdRepeatTicks;
  }
}

/**
 * The shared cooldown runs down after the manual hits of phase 1: a hit at dispatch of tick N (cooldown set
 * in phase 0) and a hit in phase 1 of tick N both allow the next one at tick N + holdRepeatTicks.
 */
function cooldownDown(c: Ctx): void {
  if (c.s.transient.tapCd > 0) c.s.transient.tapCd -= 1;
}

function holdStep(c: Ctx): void {
  const h = c.s.transient.hold;
  if (h === null) return;
  const dep = depositAt(c, h.x, h.y);
  if (dep === null) {
    c.s.transient.hold = null;
    return;
  }
  const m = c.d.economy.manual;
  if (h.t < m.holdRepeatTicks) h.t += 1;
  if (h.t >= m.holdRepeatTicks) {
    if (m.sharedCooldown && c.s.transient.tapCd > 0) return; // due, but a tap just hit: wait for it
    h.t = 0;
    c.s.progress.holdHits += 1;
    hitDeposit(c, dep, m.tapDamageMilli, 'hold', 0);
    if (m.sharedCooldown) c.s.transient.tapCd = m.holdRepeatTicks;
  }
}

function drillStep(c: Ctx, b: DrillB): void {
  if (b.ghost !== null || b.paused) return;
  const n = stepCell(b.x, b.y, b.dir);
  const dep = depositAt(c, n.x, n.y);
  if (dep === null) {
    b.t = 0;
    raiseStall(c, b, 'NO_TARGET');
    return;
  }
  if (b.stall === 'NO_TARGET') clearStall(c, b);
  const def = c.d.buildings.drill;
  b.t += 1;
  if (b.t >= (def.hitTicks[b.level - 1] as Int)) {
    b.t = 0;
    hitDeposit(c, dep, def.powerMilli[b.level - 1] as Int, 'drill', b.id);
  }
}

function spawnStep(c: Ctx, dep: DepositB): void {
  const def = depositDef(c.d, dep.ore);
  const hard = def.hardMilli;
  const rules = c.d.depositRules;
  const odds = oddsForLevel(c.d, dep.level);
  let n = 0;
  while (dep.acc >= hard && n < rules.maxSpawnsPerTick) {
    const next = mb32Next(c.s.rng);
    const tier = rollTier(mb32Out(next), odds);
    const out = depositOutput(c, dep, def.fam, tier);
    if (out === null) {
      dep.acc = hard;
      if (!dep.blocked) {
        dep.blocked = true;
        c.ev.push({ e: 'bonk', bid: dep.id, x: dep.x, y: dep.y });
      }
      raiseStall(c, dep, 'VEIN_BLOCKED');
      break;
    }
    c.s.rng = next;
    dep.rr = (out.dirIdx + 1) % 4;
    dep.acc -= hard;
    n++;
    if (dep.blocked) {
      dep.blocked = false;
      clearStall(c, dep);
    }
    book(c.s, c.d, def.fam, tier, 'mined', 1);
    if (out.k === 'floor') {
      const it = addItem(c, def.fam, tier, out.x, out.y);
      discover(c, def.fam, tier);
      c.ev.push({ e: 'spawn', id: it.id, fam: def.fam, tier, x: out.x, y: out.y, from: dep.id });
    } else {
      const b = findBuilding(c.s, out.bid) as Building;
      const r = makeRef(c, def.fam, tier);
      c.ev.push({ e: 'spawn', id: r.id, fam: def.fam, tier, x: b.x, y: b.y, from: dep.id });
      insertInto(c, b, r, 0);
    }
  }
  const cap = rules.accCapHardness * hard;
  if (dep.acc > cap) dep.acc = cap;
}

export function runDeposits(c: Ctx): void {
  landBufferedTaps(c);
  holdStep(c);
  cooldownDown(c);
  for (const b of c.s.buildings) if (b.type === 'drill') drillStep(c, b);
  for (const b of c.s.buildings) if (b.type === 'deposit') spawnStep(c, b);
}
